import { useCallback, useEffect, useRef } from 'react';
import { useSeekRequest, useYoutubePlayerStore } from '@/shared/store/youtube-player';
import type { VideoInfoBlock as VideoInfoBlockType } from '@/shared/types/blocks';

// YouTube IFrame Player 상태 상수
const YT_PLAYER_STATE = {
  PLAYING: 1,
} as const;

type VideoInfoBlockProps = Omit<VideoInfoBlockType, 'type' | 'id'>;

// ============================================================================
// YOUTUBE API LOADER (싱글톤)
// ============================================================================

let apiLoadPromise: Promise<void> | null = null;

/**
 * YouTube IFrame API를 동적으로 로드합니다.
 * 이미 로드된 경우 즉시 resolve됩니다.
 */
function loadYouTubeAPI(): Promise<void> {
  // 이미 로드됨
  if (window.YT?.Player) {
    return Promise.resolve();
  }

  // 로딩 중
  if (apiLoadPromise) {
    return apiLoadPromise;
  }

  // 새로 로드
  apiLoadPromise = new Promise((resolve) => {
    // 이미 스크립트가 있는 경우 (다른 컴포넌트에서 추가됨)
    const existingScript = document.querySelector(
      'script[src="https://www.youtube.com/iframe_api"]',
    );
    if (existingScript) {
      // API가 이미 준비된 경우
      if (window.YT?.Player) {
        resolve();
        return;
      }
      // 기존 콜백에 체인
      const originalCallback = window.onYouTubeIframeAPIReady;
      window.onYouTubeIframeAPIReady = () => {
        originalCallback?.();
        resolve();
      };
      return;
    }

    // 새 스크립트 추가
    const script = document.createElement('script');
    script.src = 'https://www.youtube.com/iframe_api';
    script.async = true;

    window.onYouTubeIframeAPIReady = () => {
      resolve();
    };

    document.head.appendChild(script);
  });

  return apiLoadPromise;
}

// ============================================================================
// COMPONENT
// ============================================================================

/**
 * VideoInfoBlock - 비디오 정보 카드
 *
 * YouTube IFrame API를 사용하여 재생 시간을 추적하고,
 * TranscriptBlock과 연동하여 자동 스크롤 및 클릭 점프를 지원합니다.
 *
 * Figma 디자인 기준:
 * - 영상: aspect-ratio 유지, rounded-[6px], iframe 또는 썸네일
 * - 제목: 16px bold
 * - 채널 아바타: 24px
 * - 채널명: 12px bold
 * - 조회수: 12px regular, #7c7e80
 */
export function VideoInfoBlock({
  videoId,
  thumbnailUrl,
  title,
  channel,
  stats,
  view_count,
  upload_date,
}: VideoInfoBlockProps) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const playerContainerRef = useRef<HTMLDivElement>(null);
  // biome-ignore lint/suspicious/noExplicitAny: YouTube IFrame API 타입
  const playerRef = useRef<any>(null);
  const isPlayerReadyRef = useRef<boolean>(false);
  const timeUpdateIntervalRef = useRef<number | null>(null);
  const lastSeekIdRef = useRef<number | null>(null);

  // Store actions
  const {
    updateCurrentTime,
    setPlayState,
    clearSeekRequest,
    removePlayer,
    registerVideoRef,
    unregisterVideoRef,
  } = useYoutubePlayerStore();

  // Subscribe to seek requests
  const seekRequest = useSeekRequest(videoId);

  // stats가 없으면 view_count와 upload_date로 조합
  const displayStats = stats || [view_count, upload_date].filter(Boolean).join(' • ') || undefined;

  // ============================================================================
  // TIME UPDATE INTERVAL
  // ============================================================================

  const startTimeUpdateInterval = useCallback(() => {
    if (timeUpdateIntervalRef.current) {
      clearInterval(timeUpdateIntervalRef.current);
    }

    if (!videoId) return;

    timeUpdateIntervalRef.current = window.setInterval(() => {
      if (playerRef.current && isPlayerReadyRef.current) {
        try {
          const state = playerRef.current.getPlayerState();
          // PLAYING 상태일 때만 업데이트 (성능 최적화)
          if (state === YT_PLAYER_STATE.PLAYING) {
            const currentTime = playerRef.current.getCurrentTime();
            updateCurrentTime(videoId, currentTime);
          }
        } catch {
          // Player not ready or destroyed
        }
      }
    }, 500);
  }, [videoId, updateCurrentTime]);

  const clearTimeUpdateInterval = useCallback(() => {
    if (timeUpdateIntervalRef.current) {
      clearInterval(timeUpdateIntervalRef.current);
      timeUpdateIntervalRef.current = null;
    }
  }, []);

  // ============================================================================
  // YOUTUBE PLAYER INITIALIZATION
  // ============================================================================

  useEffect(() => {
    if (!videoId || !playerContainerRef.current) return;

    let mounted = true;

    async function initPlayer() {
      await loadYouTubeAPI();
      if (!mounted || !playerContainerRef.current) return;

      // 기존 플레이어 정리
      if (playerRef.current) {
        try {
          playerRef.current.destroy();
        } catch {
          // Ignore destroy errors
        }
        playerRef.current = null;
      }

      // 컨테이너에 고유 ID 부여
      const containerId = `yt-player-${videoId}`;
      playerContainerRef.current.id = containerId;

      // YouTube Player 생성
      playerRef.current = new window.YT.Player(containerId, {
        videoId,
        playerVars: {
          autoplay: 0,
          modestbranding: 1,
          rel: 0,
          enablejsapi: 1,
          origin: window.location.origin,
        },
        events: {
          onReady: (event: { target: { getDuration: () => number } }) => {
            if (!mounted || !videoId) return;
            isPlayerReadyRef.current = true;
            const duration = event.target.getDuration();
            setPlayState(videoId, false, duration);
            startTimeUpdateInterval();
          },
          onStateChange: (event: { data: number; target: { getDuration: () => number } }) => {
            if (!mounted || !videoId) return;
            const isPlaying = event.data === YT_PLAYER_STATE.PLAYING;
            const duration = event.target.getDuration();
            setPlayState(videoId, isPlaying, duration);
          },
        },
      });
    }

    initPlayer();

    return () => {
      mounted = false;
      isPlayerReadyRef.current = false;
      clearTimeUpdateInterval();

      if (playerRef.current) {
        try {
          playerRef.current.destroy();
        } catch {
          // Ignore destroy errors
        }
        playerRef.current = null;
      }

      if (videoId) {
        removePlayer(videoId);
      }
    };
  }, [videoId, setPlayState, startTimeUpdateInterval, clearTimeUpdateInterval, removePlayer]);

  // ============================================================================
  // SEEK REQUEST HANDLING
  // ============================================================================

  useEffect(() => {
    if (!seekRequest || !playerRef.current || !videoId) return;
    // 플레이어가 준비되지 않았으면 무시 (postMessage 경고 방지)
    if (!isPlayerReadyRef.current) return;

    // 중복 실행 방지
    if (seekRequest.requestId === lastSeekIdRef.current) return;
    lastSeekIdRef.current = seekRequest.requestId;

    try {
      playerRef.current.seekTo(seekRequest.seconds, true);
      // 재생 시작 (클릭하면 자동 재생)
      playerRef.current.playVideo();
    } catch {
      // Player not ready
    }

    clearSeekRequest(videoId, seekRequest.requestId);
  }, [seekRequest, videoId, clearSeekRequest]);

  // ============================================================================
  // VIDEO REF REGISTRATION (스크롤용)
  // ============================================================================

  useEffect(() => {
    if (!videoId || !wrapperRef.current) return;

    registerVideoRef(videoId, wrapperRef.current);

    return () => {
      unregisterVideoRef(videoId);
    };
  }, [videoId, registerVideoRef, unregisterVideoRef]);

  // ============================================================================
  // RENDER
  // ============================================================================

  return (
    <div ref={wrapperRef} className="flex flex-col gap-4 min-w-0 w-full">
      {/* 영상 플레이어 또는 썸네일 - 16:9 비율 유지 */}
      <div
        className="relative w-full overflow-hidden rounded-[6px] bg-black"
        style={{ aspectRatio: '16/9' }}
      >
        {videoId ? (
          <div ref={playerContainerRef} className="absolute inset-0 w-full h-full" />
        ) : (
          <img src={thumbnailUrl} alt={title} className="w-full h-full object-cover" />
        )}
      </div>

      {/* 비디오 정보 */}
      <div className="flex flex-col gap-2">
        {/* 제목 */}
        <h3 className="text-[16px] font-bold text-black leading-[1.35]">{title}</h3>

        {/* 채널 정보 */}
        <div className="flex items-center gap-2">
          {/* 채널 아바타 */}
          {channel.avatar && (
            <img
              src={channel.avatar}
              alt={channel.name}
              className="w-6 h-6 rounded-full object-cover shrink-0"
            />
          )}

          {/* 채널명 + 통계 */}
          <div className="flex items-center gap-2 text-[12px] leading-[1.35]">
            <span className="font-bold text-black">{channel.name}</span>
            {displayStats && <span className="font-normal text-[#7c7e80]">{displayStats}</span>}
          </div>
        </div>
      </div>
    </div>
  );
}
